import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  Pen,
  Trash,
  ChevronDown,
  ChevronUp,
  Loader2,
  MoreHorizontal,
  UserCog,
  GitFork,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ReviewTaskDialog } from "./ReviewTaskDialog";
import { AcceptTaskDialog } from "./AcceptTaskDialog";
import { ActivityActionButton } from "./ActivityActionButton";
import { AdjustmentDialog } from "./AdjustmentDialog";
import { FinishAdjustmentDialog } from "./FinishAdjustmentDialog";
import { ReassignStaffDialog } from "./ReassignStaffDialog";
import type { Task } from "../types";
import {
  canStartStopActivity,
  canSendToReview,
  canReject,
  canCreateAdjustment,
  canReassign,
  getMatterMembershipRole,
  adjustmentFinishBlockKey,
} from "../utils";

export interface ActiveActivity {
  id: number;
  taskId: number;
}

export interface UserInfo {
  id: number;
  role?: {
    name?: string;
    type?: string;
  };
}

export interface TaskActionsCellProps {
  task: Task;
  activeActivity?: ActiveActivity | null;
  userInfo?: UserInfo | null;
  hasAccess: (resource: string, action: string) => boolean;
  expandedTasks: Record<number, boolean>;
  loadingTasks: Record<number, boolean>;
  onToggleExpand?: (taskId: number) => void;
  onEdit?: (task: Task) => void;
  onDelete?: (task: Task) => void;
  onStartActivity?: () => void;
  onStopActivity?: () => void;
  onActionSuccess?: () => void;
}

export function TaskActionsCell({
  task,
  activeActivity,
  userInfo,
  hasAccess,
  expandedTasks,
  loadingTasks,
  onToggleExpand,
  onEdit,
  onDelete,
  onStartActivity,
  onStopActivity,
  onActionSuccess,
}: TaskActionsCellProps) {
  const { t } = useTranslation();

  // Controlled open state for dialogs launched from the "More" menu. The dialogs
  // are rendered as siblings of the menu (not inside it) and toggled via state, so
  // closing the menu doesn't unmount them.
  const [reassignOpen, setReassignOpen] = React.useState(false);
  const [createAdjustmentOpen, setCreateAdjustmentOpen] = React.useState(false);

  // Viewer-owned adjustment on this parent — backend inlines this so the parent row
  // can render adjustment-targeted Start/Stop/Finish without an N+1 fetch.
  const viewerAdj = !task.isAdjustment ? (task.viewerAdjustment ?? null) : null;
  const viewerAdjUnfinished =
    viewerAdj != null && viewerAdj.adjustmentPercent == null;
  // Start/Stop on a parent row controls the adjustment activities when the viewer
  // has an unfinished adjustment here; otherwise it controls the parent task.
  const activityTargetTaskId = viewerAdj?.id ?? task.id;

  const isActiveOnActivityTarget =
    activeActivity?.taskId === activityTargetTaskId;
  const isAssignedToCurrentUser = task.assignedStaffId === userInfo?.id;
  const isCreatedByCurrentUser = task.createdBy?.id === userInfo?.id;
  const hasActivities =
    (task.activities?.filter((a) => a.status !== "REJECT")?.length ?? 0) > 0;
  // Caller's role ON THIS MATTER (lead/partner/staff). Reviewer-side actions
  // (reject, etc.) are gated by this — not by global role — so a LEAD-global
  // user who is only `staff` on the matter cannot reject. Undefined when the
  // user isn't on the matter team or the team data isn't in the payload.
  const matterMembershipRole = getMatterMembershipRole(task.matter, userInfo?.id);
  const isExpanded = expandedTasks[task.id] ?? false;
  const isLoadingActivities = loadingTasks[task.id] ?? false;
  const roleType = userInfo?.role?.type;

  const isMdPartner = roleType === "MD";

  // Terminal statuses — once a task is approved/billed, no actions apply. We
  // gate every button below on this so the row collapses to the expand-chevron
  // only (history view). Server also rejects any mutating action on these statuses.
  const isTerminalStatus =
    task.newStatus === "DONE" || task.newStatus === "BILLED";

  // The viewer still sees this task but is no longer on the matter team — they
  // were removed or moved off. Show an explanatory message instead of action
  // buttons; the server would reject any action anyway. Restricted to the task's
  // OWN ASSIGNEE — only the person the task is assigned to is told they're no
  // longer on the matter (an adjustment owner / passer-by who isn't on the team
  // just sees no actions). MD oversees every matter without team membership, so
  // MD is exempt. Only applies once the matter team data is in the payload
  // (otherwise fall back to the normal action gates).
  const matterTeamLoaded =
    Array.isArray(task.matter?.staffs) ||
    Array.isArray(task.matter?.responsibleAttorneys) ||
    Array.isArray(task.matter?.partners);
  const showNotInMatter =
    matterTeamLoaded &&
    matterMembershipRole === undefined &&
    !isMdPartner &&
    isAssignedToCurrentUser;

  // Read-only because the matter is closed or its contract is closed/terminated —
  // no time tracking or other mutating actions are allowed. The actions cell shows
  // an explanatory text instead of any buttons (server also blocks the actions).
  // `phase` is the matter lifecycle key (legacy `status` is dead — Stage 14).
  const matterClosed = task.matter?.phase === "CLOSED";
  const contractClosed =
    task.matter?.contract?.status === "CLOSED" ||
    task.matter?.contract?.status === "TERMINATED";
  const showClosed = matterClosed || contractClosed;

  // Start/Stop visibility:
  //  - if viewer owns an adjustment on this parent → always show, target adjustment.
  //    (Activities can be logged on the adjustment regardless of whether it has
  //    been "finished" — finishing only records the % changed, it doesn't lock work.)
  //  - else default rule: parent's assignee within an allowed status.
  const showStartStop =
    !isTerminalStatus &&
    (viewerAdj
      ? true
      : isAssignedToCurrentUser &&
        canStartStopActivity(roleType, task.newStatus, matterMembershipRole));

  // Synthetic Task to pass to ActivityActionButton so it starts/stops on the
  // adjustment task id rather than the parent task id.
  const activityTask: Task = viewerAdj
    ? ({ ...task, id: viewerAdj.id, isAdjustment: true } as Task)
    : task;

  // Send-to-Review is shown to anyone with permission for this status (per the
  // role/status matrix in canSendToReview) — not just the assignee. This lets
  // lead/partner approve & forward from the review boards (Lead Review, Partner
  // Review) on tasks assigned to others. It is gated by the caller's MATTER-
  // membership role (not global role): a LEAD-global user who is only `staff`
  // on this matter must not see "Send to Partner" on a LEAD_PENDING task — the
  // server would reject the forward anyway. Falls back to the global role only
  // when the matter team isn't in the payload (server still enforces).
  // Send-to-Review is hidden (not just disabled) while the viewer has an
  // UNFINISHED adjustment on this task — finishing the adjustment auto-forwards
  // the parent, so a manual Send-to-Review would be redundant. Once the
  // adjustment is finished (or there is none) the normal rules apply.
  const showReview =
    !isTerminalStatus &&
    !viewerAdjUnfinished &&
    canSendToReview(roleType, task.newStatus, matterMembershipRole);
  // Reject is a reviewer-side action — the matter's lead/partner reviewing a
  // task pushes it back to the assignee. The assignee themselves never rejects
  // their own task, so hide whenever the caller IS the assignee. Reviewer
  // authority comes from the caller's matter-membership role, not their global
  // role: a LEAD-global user who isn't `responsibleAttorney` here can't reject.
  // Also hidden whenever the viewer has their OWN adjustment on this task
  // (finished or not) — once they've taken on adjustment work they finish it
  // (which forwards the task) rather than rejecting it back. **MD is exempt**
  // (user request 2026-06-07): the final approver may push a task back from
  // Approve to Billing even while owning an adjustment on it (the server never
  // gated rejects on adjustments — only forwards/accept).
  const showReject =
    !isTerminalStatus &&
    !isAssignedToCurrentUser &&
    (isMdPartner || !viewerAdj) &&
    canReject(roleType, task.newStatus, matterMembershipRole);

  const showAccept =
    !isTerminalStatus && isMdPartner && task.newStatus === "BILLING_PENDING";

  // Adjustment: show "Create adjustment" when the current user is allowed to
  // create one per the matter-membership matrix (IS-001/CR-002), does not
  // already have an adjustment on this parent (server rejects duplicates with 409),
  // AND is NOT the parent task's own assignee (an assignee logs time directly on
  // their task — an adjustment would be redundant). Hiding the button here is a
  // friendlier UX gate; the role gate falls back to the global role optimistically
  // when matter-team data isn't in the payload, and the server enforces the real
  // check (including the "not the assignee" rule).
  const showCreateAdjustment =
    !isTerminalStatus &&
    !viewerAdj &&
    !isAssignedToCurrentUser &&
    canCreateAdjustment(roleType, task.newStatus);

  // Reassign (CR-006 / IS-004) — matter-membership gated, MD global override.
  // Surfaced on any non-terminal original task the caller may reassign; the
  // target restriction (lead → staff) is enforced inside the dialog + server.
  const currentAssigneeMatterRole = getMatterMembershipRole(
    task.matter,
    task.assignedStaffId,
  );
  const showReassign =
    !isTerminalStatus &&
    !task.isAdjustment &&
    hasAccess("task", "update") &&
    canReassign(
      roleType,
      task.newStatus,
      matterMembershipRole,
      currentAssigneeMatterRole,
    );

  // "More" menu actions — the less-frequent / management actions, collapsed behind
  // an icon-only ⋯ trigger: Edit, Reassign Staff, Create Adjustment, Delete.
  const showEditAction =
    hasAccess("task", "update") &&
    task.newStatus === "NEW" &&
    isCreatedByCurrentUser;
  const showDeleteAction =
    hasAccess("task", "delete") &&
    !isTerminalStatus &&
    (isMdPartner || (task.newStatus === "NEW" && isCreatedByCurrentUser));
  const showMoreMenu =
    showEditAction || showReassign || showCreateAdjustment || showDeleteAction;

  // Show "Finish adjustment" on:
  //  (a) the adjustment row itself when listed standalone (legacy), or
  //  (b) the parent row when the viewer's adjustment is inlined and unfinished.
  const showFinishAdjustmentOnAdjustmentRow =
    !isTerminalStatus &&
    task.isAdjustment === true &&
    task.assignedStaffId === userInfo?.id &&
    task.adjustmentPercent == null;
  const showFinishViewerAdjustment = !isTerminalStatus && viewerAdjUnfinished;

  // Synthetic adjustment Task for the parent-row Finish dialog.
  const viewerAdjAsTask: Task | null = viewerAdj
    ? ({
        ...task,
        id: viewerAdj.id,
        isAdjustment: true,
        adjustmentPercent: viewerAdj.adjustmentPercent ?? null,
      } as Task)
    : null;

  // Finish-adjustment is disabled until the adjustment has logged activity AND no
  // timer is still running on it (must stop first). Same rule for both variants.
  const adjustmentRowFinishBlockKey = adjustmentFinishBlockKey(task.activities);
  const viewerAdjFinishBlockKey = adjustmentFinishBlockKey(viewerAdj?.activities);

  const getReviewDisabledReason = (): string | undefined => {
    // Block while the parent has a running timer (must stop first).
    if (activeActivity?.taskId === task.id) return t("stopActivityBeforeReview");
    // Activity-emptiness only blocks forwards when the caller IS the task's
    // assignee — they're the one expected to log time. Reviewers forwarding /
    // rejecting another user's task aren't expected to have logged activity here
    // (lead/partner approvals from review boards, MD reject, etc.).
    if (isAssignedToCurrentUser && !hasActivities) return t("noActivitiesToReview");
    return undefined;
  };

  const reviewDisabledReason = getReviewDisabledReason();

  // Forwarding (send-to-review/billing) and MD approve are additionally blocked
  // while the viewer has an unfinished adjustment on this task — but REJECT is not
  // (matches the backend, which only gates the forward + accept actions).
  const forwardDisabledReason = task.viewerHasUnfinishedAdjustment
    ? t("adjustment.finishFirst")
    : reviewDisabledReason;

  // MD Accept (BILLING_PENDING → billed): unlike forwarding, the approver isn't
  // the assignee, so the assignee-only no-activities gate above never fires for
  // MD. But the backend rejects accepting a task with no logged activity time
  // ("Cannot accept task without any activity time"), so disable the button up
  // front when the task has no (non-rejected) activities — keeping the running-
  // timer / unfinished-adjustment blocks from forwardDisabledReason too.
  const acceptDisabledReason =
    forwardDisabledReason ??
    (!hasActivities ? t("noActivitiesToAccept") : undefined);

  return (
    <div className="flex gap-1.5 items-center flex-nowrap whitespace-nowrap">
      {/* Expand/Collapse Activities Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onToggleExpand?.(task.id)}
        className="h-8 w-8 p-0"
        disabled={isLoadingActivities}
      >
        {isLoadingActivities ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : isExpanded ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </Button>

      {showClosed ? (
        <span className="text-xs italic text-muted-foreground">
          {matterClosed ? t("matterClosed") : t("contractClosed")}
        </span>
      ) : showNotInMatter ? (
        <span className="text-xs italic text-muted-foreground">
          {t("notInMatterAnymore")}
        </span>
      ) : (
        <>
      {/* Stop Activity */}
      {isActiveOnActivityTarget && hasAccess("activity", "stop") && showStartStop && (
        <ActivityActionButton
          task={activityTask}
          activityId={activeActivity!.id}
          onSuccess={onStopActivity}
          isStop
        />
      )}

      {/* Start Activity */}
      {!isActiveOnActivityTarget &&
        hasAccess("activity", "start") &&
        showStartStop && (
          <ActivityActionButton task={activityTask} onSuccess={onStartActivity} />
        )}

      {/* Finish Adjustment — adjustment row variant (legacy / non-My-Tasks boards) */}
      {showFinishAdjustmentOnAdjustmentRow && (
        <FinishAdjustmentDialog
          task={task}
          disabledReason={
            adjustmentRowFinishBlockKey ? t(adjustmentRowFinishBlockKey) : undefined
          }
          onSuccess={onActionSuccess}
        />
      )}

      {/* Finish Adjustment — parent-row variant: targets the inlined viewer adjustment */}
      {showFinishViewerAdjustment && viewerAdjAsTask && (
        <FinishAdjustmentDialog
          task={viewerAdjAsTask}
          disabledReason={
            viewerAdjFinishBlockKey ? t(viewerAdjFinishBlockKey) : undefined
          }
          onSuccess={onActionSuccess}
        />
      )}

      {/* Send to Review */}
      {showReview && (
        <ReviewTaskDialog
          task={task}
          mode="review"
          disabledReason={forwardDisabledReason}
          onSuccess={onActionSuccess}
        />
      )}

      {/* Reject */}
      {showReject && (
        <ReviewTaskDialog
          task={task}
          mode="reject"
          disabledReason={reviewDisabledReason}
          onSuccess={onActionSuccess}
        />
      )}

      {/* Accept Task (MD — direct accept, no QA step) */}
      {showAccept && (
        <AcceptTaskDialog task={task} onSuccess={onActionSuccess} disabledReason={acceptDisabledReason} />
      )}

      {/* More — collapses the management actions (Edit, Reassign, Create
          Adjustment, Delete) behind an icon-only ⋯ trigger. The two dialog-based
          actions (Reassign / Create Adjustment) are launched via controlled state
          and rendered as siblings below so the menu can close cleanly. */}
      {showMoreMenu && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              aria-label={t("openMenu")}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[180px]">
            {/* Edit — creator may edit their own NEW task */}
            {showEditAction && (
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => onEdit?.(task)}
              >
                <Pen className="mr-2 h-4 w-4" />
                <span>{t("edit")}</span>
              </DropdownMenuItem>
            )}

            {/* Reassign Staff — matter-membership gated, work-preserving */}
            {showReassign && (
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => setReassignOpen(true)}
              >
                <UserCog className="mr-2 h-4 w-4" />
                <span>{t("reassignStaff")}</span>
              </DropdownMenuItem>
            )}

            {/* Create Adjustment — when the caller's matter role allows it */}
            {showCreateAdjustment && (
              <DropdownMenuItem
                className="cursor-pointer text-amber-600 focus:text-amber-700 focus:bg-amber-50"
                onClick={() => setCreateAdjustmentOpen(true)}
              >
                <GitFork className="mr-2 h-4 w-4" />
                <span>{t("adjustment.createAdjustment")}</span>
              </DropdownMenuItem>
            )}

            {/* Delete — creator on a NEW task, or MD on any non-terminal task */}
            {showDeleteAction && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="cursor-pointer text-red-600 focus:text-red-700 focus:bg-red-50"
                  onClick={() => onDelete?.(task)}
                >
                  <Trash className="mr-2 h-4 w-4" />
                  <span>{t("delete")}</span>
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Dialogs launched from the More menu (rendered as siblings, controlled). */}
      {showReassign && (
        <ReassignStaffDialog
          task={task}
          open={reassignOpen}
          onOpenChange={setReassignOpen}
          hideTrigger
          onSuccess={onActionSuccess}
        />
      )}
      {showCreateAdjustment && (
        <AdjustmentDialog
          task={task}
          open={createAdjustmentOpen}
          onOpenChange={setCreateAdjustmentOpen}
          hideTrigger
          onSuccess={onActionSuccess}
        />
      )}
        </>
      )}
    </div>
  );
}
